import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SupabaseStatus } from '@optidev/shared-types';
import { ConnectionInfoTab } from './tabs/ConnectionInfoTab';
import { DatabaseTab } from './tabs/DatabaseTab';
import { AuthUsersTab } from './tabs/AuthUsersTab';
import { EdgeFunctionsSecretsTab } from './tabs/EdgeFunctionsSecretsTab';
import { StorageTab } from './tabs/StorageTab';
import { LogsTab } from './tabs/LogsTab';
import { AdvisorsTab } from './tabs/AdvisorsTab';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

interface CloudManagementProps {
  projectId: string;
  status: SupabaseStatus;
}

type TabType = 'connection-info' | 'database' | 'auth' | 'functions' | 'storage' | 'logs' | 'advisors';

const VALID_CLOUD_TABS: TabType[] = ['connection-info', 'database', 'auth', 'functions', 'storage', 'logs', 'advisors'];

function isValidTabType(value: string): value is TabType {
  return VALID_CLOUD_TABS.includes(value as TabType);
}

export function CloudManagement({ projectId, status }: CloudManagementProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const cloudTabParam = searchParams.get('cloudTab') as string | null;
  const { isMobile } = useResponsive();

  const [activeTab, setActiveTab] = useState<TabType>(
    cloudTabParam && isValidTabType(cloudTabParam) ? cloudTabParam : 'connection-info'
  );

  useEffect(() => {
    if (cloudTabParam && isValidTabType(cloudTabParam) && cloudTabParam !== activeTab) {
      setActiveTab(cloudTabParam);
    }
  }, [cloudTabParam]);

  const tabs = [
    { id: 'connection-info' as TabType, label: 'Connection' },
    { id: 'auth' as TabType, label: 'Authentication' },
    { id: 'functions' as TabType, label: 'Edge Functions' },
    { id: 'database' as TabType, label: 'Database' },
    { id: 'storage' as TabType, label: 'File Storage' },
    { id: 'logs' as TabType, label: 'Logs' },
    { id: 'advisors' as TabType, label: 'Advisors' },
  ];

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('cloudTab', tabId);
    setSearchParams(newParams, { replace: true });
  };

  const getStatusColor = () => {
    switch (status) {
      case SupabaseStatus.ACTIVE_HEALTHY:
        return 'bg-emerald-500';
      case SupabaseStatus.PROVISIONING:
      case SupabaseStatus.RESTORING:
        return 'bg-blue-500 animate-pulse';
      case SupabaseStatus.PAUSED:
        return 'bg-amber-500';
      case SupabaseStatus.FAILED:
        return 'bg-red-500';
      default:
        return 'bg-zinc-400';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case SupabaseStatus.ACTIVE_HEALTHY:
        return 'Active';
      case SupabaseStatus.PROVISIONING:
        return 'Provisioning';
      case SupabaseStatus.RESTORING:
        return 'Restoring';
      case SupabaseStatus.PAUSED:
        return 'Paused';
      case SupabaseStatus.FAILED:
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with title - hidden on mobile since shown in parent sheet */}
      {!isMobile && (
        <div className="flex-shrink-0 px-8 pt-3 pb-5 border-b border-border">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold">OptiDev Cloud</h1>
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/50">
              <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor()}`} />
              <span className="text-xs font-medium text-muted-foreground">{getStatusLabel()}</span>
            </div>
          </div>
          <p className="text-muted-foreground mt-1">
            Managed backend services for your project
          </p>
        </div>
      )}

      {/* Tabs - horizontal scroll on mobile with better styling */}
      <div className={cn(
        "flex-shrink-0 border-b border-border",
        isMobile ? "px-4 pb-2" : "px-8 py-4"
      )}>
        <div className={cn(
          "flex gap-1.5 overflow-x-auto scrollbar-hide",
          isMobile && "-mx-4 px-4"
        )}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "font-medium rounded-lg transition-colors whitespace-nowrap",
                isMobile ? "px-3 py-2 text-xs" : "px-3 py-1.5 text-sm border",
                activeTab === tab.id
                  ? isMobile
                    ? 'bg-purple-600 text-white'
                    : 'bg-muted text-foreground border-foreground/10'
                  : isMobile
                    ? 'bg-muted text-muted-foreground'
                    : 'border-border/60 text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className={cn(
        'flex-1',
        (activeTab === 'database' || activeTab === 'auth' || (activeTab === 'functions' && !isMobile))
          ? 'overflow-hidden'
          : 'overflow-auto'
      )}>
        <div className={cn(
          (activeTab === 'database' || activeTab === 'auth' || (activeTab === 'functions' && !isMobile)) ? 'h-full' : '',
          isMobile ? 'px-4 py-4' : 'px-8 py-6'
        )}>
          {activeTab === 'connection-info' && <ConnectionInfoTab projectId={projectId} />}
          {activeTab === 'database' && <DatabaseTab projectId={projectId} />}
          {activeTab === 'auth' && <AuthUsersTab projectId={projectId} />}
          {activeTab === 'functions' && <EdgeFunctionsSecretsTab projectId={projectId} />}
          {activeTab === 'storage' && <StorageTab projectId={projectId} />}
          {activeTab === 'logs' && <LogsTab projectId={projectId} initialFunctionSlug={searchParams.get('functionSlug') || undefined} />}
          {activeTab === 'advisors' && <AdvisorsTab projectId={projectId} />}
        </div>
      </div>
    </div>
  );
}
